import { UnorderedListOutlined } from "@ant-design/icons";
import { Divider } from "antd";
import { Button, List, Modal } from "antd";
import dayjs from "dayjs";
import React from "react";
import {
  Icon,
  LandingIcon,
  StartIcon,
  vak_half,
  vak_kwart,
} from "~/components/icon/icon.jsx";
import { $t } from "~/i18n.js";

export const KistStatusQueueModal = ({
  open,
  onClose,
  kist,
  createNewFlightWithKist,
  openFlightUUID,
  updateFlight,
}) => {
  if (!kist) return null;

  const [loading, setLoading] = React.useState(null);

  const closeAndClear = () => {
    setLoading(null);
    onClose()
  };

  const handleOpenFlight = (flightUuid) => {
    openFlightUUID(flightUuid);
    onClose();
  };

  const handleStart = async (flight) => {
    await updateFlight({
      uuid: flight.flight_uuid,
      start_tijd: dayjs().format("HH:mm"),
    });

    // also sleep the start of the sleep flight if it exists
    if (flight.flight?.sleep_uuid) {
      await updateFlight({
        uuid: flight.flight.sleep_uuid,
        start_tijd: dayjs().format("HH:mm"),
      });
    }
  };

  const handleStartFlight = async () => {
    setLoading("start");
    await handleStart(kist.active_flights[0]);
    closeAndClear()
  };

  const handleStartAndPlanNext = async () => {
    setLoading("start-plan");
    await handleStart(kist.active_flights[0]);
    await createNewFlightWithKist({ kist });
    closeAndClear()
  };

  const saveLandingTime = async (flight) => {
    await updateFlight({
      uuid: flight.flight_uuid,
      start_tijd: flight.flight?.start_tijd,
      landings_tijd: dayjs().format("HH:mm"),
      // only set action if no landingstime has been set, in which case server will overrule landingstime
      action: "land",
    });
  };

  const handleLandFlight = async () => {
    setLoading("land");
    await saveLandingTime(kist.active_flights[0]);
    closeAndClear()
  };

  const handleLandAndPlanNext = async () => {
    setLoading("land-plan");
    await saveLandingTime(kist.active_flights[0]);
    await createNewFlightWithKist({ kist });
    closeAndClear()
  };


  const handlePlanNext = async () => {
    setLoading("plan");
    await createNewFlightWithKist({ kist });
    closeAndClear()
  };

  // Check if we should show start buttons
  const currentFlight = kist.active_flights?.[0];
  const canStart =
    currentFlight &&
    (currentFlight.status === "in_grid" || currentFlight.status === "assigned");
  const canLand = currentFlight && currentFlight.status === "in_air";
  const canPlanNext = kist.active_flights?.length === 1;

  return (
    <Modal
      title={`${kist.callsign} - ${$t("Wachtlijst")}`}
      open={open}
      onClose={onClose}
      onCancel={onClose}
      width={450}
      footer={null}
    >
      <List
        bordered={false}
        dataSource={kist.active_flights || []}
        locale={{
          emptyText: (
            <p
              style={{
                textAlign: "center",
                color: "#999",
                fontStyle: "italic",
              }}
            >
              {$t("Geen actieve vluchten")}
            </p>
          ),
        }}
        renderItem={(flightData, index) => (
          <List.Item
            key={index}
            actions={[
              <Button
                key="open"
                type="default"
                onClick={() => handleOpenFlight(flightData.flight_uuid)}
              >
                {$t("Open")}
              </Button>,
            ]}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {!flightData.flight.gezagvoerder_naam && (
                <p>
                  <Icon
                    icon={vak_kwart}
                    style={{ marginRight: 4 }}
                    color="gray"
                  />
                  <em>{$t("Niet ingedeeld")}</em>
                </p>
              )}

              {flightData.flight.gezagvoerder_naam && (
                <p>
                  <Icon
                    icon={vak_half}
                    style={{ marginRight: 4 }}
                    color="gray"
                  />
                  {flightData.flight.gezagvoerder_naam}
                </p>
              )}

              <p>
                <StartIcon style={{ marginRight: 5 }} color="gray" />
                {flightData.flight.start_tijd ? (
                  flightData.flight.start_tijd
                ) : (
                  <em>{$t("Niet gestart")}</em>
                )}
              </p>
            </div>
          </List.Item>
        )}
      />

      {/* Custom button template beneath the list */}
      {(canStart || canLand) && (
        <div style={{ marginTop: 24 }}>
          <Divider style={{ marginTop: 16, marginBottom: 24 }} />
          <div
            style={{
              display: "flex",
              gap: 8,
              width: "100%",
            }}
          >
            {canStart && (
              <div style={{ flex: 1 }}>
                <Button
                  type="primary"
                  icon={<StartIcon color="white" style={{}} />}
                  onClick={handleStartFlight}
                  loading={loading === "start"}
                  disabled={loading !== null}
                  block
                >
                  {$t("Start")}
                </Button>
              </div>
            )}

            {canLand && (
              <div style={{ flex: 1 }}>
                <Button
                  type="primary"
                  onClick={handleLandFlight}
                  icon={<LandingIcon color="white" size="16" />}
                  loading={loading === "land"}
                  disabled={loading !== null}
                  block
                >
                  {$t("Land")}
                </Button>
              </div>
            )}

            {canPlanNext && canStart && (
              <div style={{ flex: 1 }}>
                <Button
                  icon={<StartIcon />}
                  onClick={handleStartAndPlanNext}
                  loading={loading === "start-plan"}
                  disabled={loading !== null}
                  block
                >
                  {$t("Start + plan")}
                </Button>
              </div>
            )}

            {canPlanNext && canLand && (
              <div style={{ flex: 1 }}>
                <Button
                  onClick={handleLandAndPlanNext}
                  loading={loading === "land-plan"}
                  disabled={loading !== null}
                  block
                >
                  {$t("Land + plan")}
                </Button>
              </div>
            )}

            {canPlanNext && canLand && (
              <div style={{ flex: 1 }}>
                <Button
                  type="default"
                  icon={<UnorderedListOutlined />}
                  onClick={handlePlanNext}
                  loading={loading === "plan"}
                  disabled={loading !== null}
                  block
                >
                  {$t("Plan volgende")}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};
